import React, { useMemo, useState } from 'react';
import { authedFetch, buildApiUrl } from './lib/utils';
import { runProposalLint } from './lib/proposal-lint';
import { ProposalQualityGate } from './components/ProposalQualityGate';
import { useToast } from './toast';

// Below this score we soft-block submission so freelancers confirm they really
// want to spend Connects on a weak draft.
const QUALITY_GATE_THRESHOLD = 40;

export const ProposalModal = ({ job, onClose, onSubmitted }) => {
  const { addToast } = useToast();
  const [coverLetter, setCoverLetter] = useState('');
  const [bidAmount, setBidAmount] = useState('');
  const [hourlyRate, setHourlyRate] = useState('');
  const [attachments, setAttachments] = useState([]);
  const [enableMilestones, setEnableMilestones] = useState(false);
  const [milestones, setMilestones] = useState([
    { title: '', amount: '', due: '' }
  ]);
  const [submitting, setSubmitting] = useState(false);
  const [lowQualityAcknowledged, setLowQualityAcknowledged] = useState(false);

  const jobType = job?.job_type || (job?.data?.job_type) || 'fixed_price';

  // Live quality score — recomputed cheaply on every keystroke.
  const qualityLint = useMemo(
    () => runProposalLint({ coverLetter, job }),
    [coverLetter, job]
  );

  const totalMilestones = useMemo(() => {
    return milestones.reduce((sum, m) => sum + (parseFloat(m.amount) || 0), 0);
  }, [milestones]);

  const onAddMilestone = () => {
    setMilestones((prev) => [...prev, { title: '', amount: '', due: '' }]);
  };
  const onRemoveMilestone = (idx) => {
    setMilestones((prev) => prev.filter((_, i) => i !== idx));
  };
  const updateMilestone = (idx, field, value) => {
    setMilestones((prev) => prev.map((m, i) => (i === idx ? { ...m, [field]: value } : m)));
  };

  const handleFiles = (e) => {
    const files = Array.from(e.target.files || []);
    setAttachments(files);
  };

   const handleSubmit = async () => {
    try {
      setSubmitting(true);
      
      // Check if job is open for proposals
      if (job?.status && job.status !== 'open') {
        addToast({ 
          type: 'error', 
          message: job.status === 'draft' 
            ? 'This job is still in draft mode and not accepting proposals yet.' 
            : `This job is ${job.status} and not accepting proposals.` 
        });
        setSubmitting(false);
        return;
      }
      
      // Validation
      if (!coverLetter.trim()) {
        addToast({ type: 'error', message: 'Please add a brief cover letter.' });
        setSubmitting(false);
        return;
      }

      // Soft quality gate — first click below the threshold reveals a warning
      // banner with the lint findings; second click submits anyway.
      if (qualityLint.score < QUALITY_GATE_THRESHOLD && !lowQualityAcknowledged) {
        setLowQualityAcknowledged(true);
        addToast({
          type: 'warning',
          message: `Proposal quality is ${qualityLint.score}/100. Review the suggestions, or click Submit again to send anyway.`,
        });
        setSubmitting(false);
        return;
      }
      if (jobType === 'fixed_price' && !enableMilestones && !bidAmount) {
        addToast({ type: 'error', message: 'Please enter your bid amount or add milestones.' });
        setSubmitting(false);
        return;
      }
      if (jobType === 'hourly' && !hourlyRate) {
        addToast({ type: 'error', message: 'Please enter your proposed hourly rate.' });
        setSubmitting(false);
        return;
      }
      if (!job?.id) {
        addToast({ type: 'error', message: 'Job information is missing.' });
        setSubmitting(false);
        return;
      }

      // Prepare proposal data
      const proposalData = {
        job_id: job.id,
        cover_letter: coverLetter.trim(),
        question_responses: {}
      };

      // Add pricing based on job type
      if (jobType === 'fixed_price') {
        if (enableMilestones && milestones.length > 0) {
          // Format milestones for API - Milestone model requires description and amount
          proposalData.milestones = milestones
            .filter(m => m.title && m.amount)
            .map(m => ({
              description: m.title, // Use title as description
              amount: parseFloat(m.amount) || 0,
              due_date: m.due || null
            }));
        } else {
          proposalData.proposed_budget = parseFloat(bidAmount) || 0;
        }
      } else if (jobType === 'hourly') {
        proposalData.proposed_rate = parseFloat(hourlyRate) || 0;
      }

      // Submit proposal to backend
      const response = await authedFetch(buildApiUrl('/api/v1/proposals/'), {
        method: 'POST',
        body: JSON.stringify(proposalData)
      });

      addToast({ type: 'success', message: 'Proposal submitted successfully!' });
      
      // Call onSubmitted callback with the response
      onSubmitted && onSubmitted(response);
    } catch (e) {
      console.error('Error submitting proposal:', e);
      
      // Try to parse error message if it's JSON
      let errorMessage = 'Something went wrong while submitting your proposal.';
      if (e.message) {
        try {
          const errorData = JSON.parse(e.message);
          const detail = errorData.detail || errorData.message || '';
          
          // Provide more specific error messages
          if (detail.includes('Failed to create proposal') || detail.includes('Failed to create')) {
            errorMessage = 'Unable to submit proposal. The job may not be accepting proposals at this time.';
          } else {
            errorMessage = detail || errorMessage;
          }
        } catch {
          // If not JSON, use the message as is (might be a plain error string)
          errorMessage = e.message.length > 150 ? errorMessage : e.message;
        }
      }
      
      addToast({ type: 'error', message: errorMessage });
    } finally {
      setSubmitting(false);
    }
  };


  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl mx-4 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
          <div>
            <h3 className="text-xl font-bold text-gray-900">Submit Proposal</h3>
            <p className="text-sm text-gray-500">{job?.title || 'Job'}</p>
          </div>
          <button onClick={onClose} className="text-gray-500 hover:text-black">✕</button>
        </div>

        <div className="p-6 space-y-6 max-h-[70vh] overflow-y-auto">
          {/* Cover Letter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Cover Letter *</label>
            <textarea
              value={coverLetter}
              onChange={(e) => {
                setCoverLetter(e.target.value);
                if (lowQualityAcknowledged) setLowQualityAcknowledged(false);
              }}
              className="w-full h-28 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-500 focus:border-gray-500"
              placeholder="Briefly explain why you’re a great fit"
            />
            <div className="mt-3">
              <ProposalQualityGate coverLetter={coverLetter} job={job} />
            </div>
            {lowQualityAcknowledged && qualityLint.score < QUALITY_GATE_THRESHOLD && (
              <div className="mt-3 px-4 py-3 border border-amber-300 bg-amber-50 rounded-lg text-sm text-amber-900">
                This proposal scored {qualityLint.score}/100. Submitting weak proposals burns Connects without
                improving your reply rate. Click <strong>Submit Proposal</strong> again to send anyway.
              </div>
            )}
          </div>

          {/* Pricing Section */}
          {jobType === 'fixed_price' ? (
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <label className="text-sm font-medium text-gray-700">Enable Milestones</label>
                <button
                  onClick={() => setEnableMilestones((v) => !v)}
                  className={`px-3 py-1 rounded-lg text-sm border ${enableMilestones ? 'bg-black text-white border-black' : 'border-gray-300 text-gray-700 hover:bg-gray-50'}`}
                >
                  {enableMilestones ? 'Enabled' : 'Add milestones'}
                </button>
              </div>

              {!enableMilestones && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Your Bid Amount (USD) *</label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={bidAmount}
                    onChange={(e) => setBidAmount(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-500 focus:border-gray-500"
                    placeholder="e.g., 5500"
                  />
                </div>
              )}

              {enableMilestones && (
                <div className="space-y-3">
                  {milestones.map((m, idx) => (
                    <div key={idx} className="grid grid-cols-1 md:grid-cols-12 gap-3 items-end">
                      <div className="md:col-span-6">
                        <label className="block text-xs text-gray-600 mb-1">Milestone Title</label>
                        <input
                          value={m.title}
                          onChange={(e) => updateMilestone(idx, 'title', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-500 focus:border-gray-500"
                          placeholder="Design, Development, QA..."
                        />
                      </div>
                      <div className="md:col-span-3">
                        <label className="block text-xs text-gray-600 mb-1">Amount (USD)</label>
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          value={m.amount}
                          onChange={(e) => updateMilestone(idx, 'amount', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-500 focus:border-gray-500"
                          placeholder="1500"
                        />
                      </div>
                      <div className="md:col-span-3">
                        <label className="block text-xs text-gray-600 mb-1">Due Date</label>
                        <input
                          type="date"
                          value={m.due}
                          onChange={(e) => updateMilestone(idx, 'due', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-500 focus:border-gray-500"
                        />
                      </div>
                      <div className="md:col-span-12 text-right">
                        {milestones.length > 1 && (
                          <button onClick={() => onRemoveMilestone(idx)} className="text-sm text-red-600 hover:text-red-700">Remove</button>
                        )}
                      </div>
                    </div>
                  ))}
                  <div className="flex justify-between items-center">
                    <button onClick={onAddMilestone} className="px-3 py-2 border border-black text-black rounded-lg hover:bg-gray-50 text-sm">+ Add Milestone</button>
                    <div className="text-sm text-gray-700">Total: <span className="font-semibold text-gray-900">${totalMilestones.toFixed(2)}</span></div>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Proposed Hourly Rate (USD) *</label>
              <input
                type="number"
                min="0"
                step="0.01"
                value={hourlyRate}
                onChange={(e) => setHourlyRate(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-500 focus:border-gray-500"
                placeholder="e.g., 65"
              />
            </div>
          )}

          {/* Attachments */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Attachments (optional)</label>
            <input type="file" multiple onChange={handleFiles} className="block w-full text-sm text-gray-600" />
            {attachments.length > 0 && (
              <div className="mt-2 text-xs text-gray-500">{attachments.length} file(s) selected</div>
            )}
          </div>
        </div>

        <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 flex justify-between items-center">
          <button onClick={onClose} className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100">Cancel</button>
          <button disabled={submitting} onClick={handleSubmit} className="bg-black text-white px-6 py-2 rounded-lg hover:bg-gray-800 font-semibold disabled:opacity-60">
            {submitting ? 'Submitting…' : 'Submit Proposal'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default { ProposalModal };